import React, { useState, useEffect } from 'react';
import './WizardLaunchStepper.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  StepConnector,
  styled,
  CircularProgress,
} from '@mui/material';
import CourseSelection from '../../enrolmentWizardUserJourney/CourseSelection/CourseSelection';
import CDLTypeSelection from '../../enrolmentWizardUserJourney/CDLTypeSelection/CDLTypeSelection';
import EndorsementSelection from '../../enrolmentWizardUserJourney/EndorsementSelection/EndorsementSelection';
import StateSelection from '../../enrolmentWizardUserJourney/StateSelection/StateSelection';
import VideoSelection from '../../enrolmentWizardUserJourney/VideoSelection/VideoSelection';
import CourseRecommendation from '../../enrolmentWizardUserJourney/CourseRecommendation/CourseRecommendation';
import MoreClassSelection from '../../enrolmentWizardUserJourney/MoreClassSelection/MoreClassSelection';
import {
  nextStep,
  prevStep,
  fetchDynamicDataRequest,
  fetchDynamicDataSuccess,
  resetDynamicData,
  setDynamicData,
  setCourseDetails,
  setCdlType,
  setEndorsements,
  setStateSelection,
  setVideoSelection,
  setCourseRecommendation,
  setCurrentStep,
  addToNavigationHistory,
  updateNavigationState,
  resetStep,
  SET_QUERY_PARAMS,
  setQueryParams,
} from '../../../redux/actions/WizardLaunchActions';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import stepRoutes from '../../../util/stepRoutes';
import Banner from '../Banner/Banner';

const CustomConnector = styled(StepConnector)({
  '& .MuiStepConnector-line': {
    // height: '4px',
    borderWidth: '6px',
    borderColor: '#E5E5E5',
  },
  '&.Mui-active': {
    '& .MuiStepConnector-line': {
      borderColor: '#FFD700',
      borderWidth: '6px',
    },
  },
  '&.Mui-completed': {
    '& .MuiStepConnector-line': {
      borderColor: '#FFD700',
      borderWidth: '6px',
    },
  },
});

const CustomStepIconRoot = styled('div')(({ ownerState }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  border: '6px solid',
  borderColor: ownerState.active ? '#E5E5E5' : '#eaeaf0',
  backgroundColor: ownerState.active ? '#fff' : '#eaeaf0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&.completed': {
    borderColor: '#FFD700',
    backgroundColor: '#FFD700',
  },
}));

function CustomStepIcon(props) {
  const { active, completed } = props;
  return (
    <CustomStepIconRoot
      ownerState={{ active }}
      className={completed ? 'completed' : ''}
    >
      {completed ? (
        <div
          style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: '#FFD700',
          }}
        />
      ) : active ? (
        <div
          style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: '#FFD700',
          }}
        />
      ) : (
        <div
          style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: '#fff',
          }}
        />
      )}
    </CustomStepIconRoot>
  );
}

function WizardLaunchStepper() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const activeStep = useSelector((state) => state.wizardLaunch.activeStep);
  const screenData = useSelector((state) => state.wizardLaunch.screenData);
  const dynamicData = useSelector((state) => state.wizardLaunch.dynamicData);
  const steps = useSelector((state) => state.wizardLaunch.steps);
  const [formState, setFormState] = useState({
    question_id: '',
    userAnswer: null,
    question_type: 'course-selection',
    isLoading: false,
    isBackNavigation: false,
  });
  const navigationPath = useSelector(
    (state) => state.wizardLaunch.navigationPath
  );
  const currentStep = useSelector((state) => state.wizardLaunch.currentStep);
  const previousStep = useSelector((state) => state.wizardLaunch.previousStep);
  const navigationHistory = useSelector(
    (state) => state.wizardLaunch.navigationHistory
  );
  const [isUpdatingStep, setIsUpdatingStep] = useState(false);

  // Parse URL query parameters and save to Redux
  useEffect(() => {
    if (location.search) {
      const queryParams = new URLSearchParams(location.search);
      const queryParamsObj = {};
      
      // Convert URLSearchParams to object
      for (const [key, value] of queryParams.entries()) {
        queryParamsObj[key] = value;
      }
      
      // Only dispatch if we have query parameters
      if (Object.keys(queryParamsObj).length > 0) {
        dispatch(setQueryParams(queryParamsObj));
        
        // Handle specific query parameters for direct navigation if needed
        if (queryParamsObj.step) {
          const stepPath = `/${queryParamsObj.step}`;
          if (stepRoutes.some(route => route.path === stepPath)) {
            navigate(stepPath);
          }
        }
      }
    }
  }, [location.search, dispatch, navigate]);

  // Define question types for each step using a configuration array
  const stepTypes = [
    'course-selection',
    'select',
    'endorsements',
    'cdl_type',
    'video',
    'recommend',
  ];

  function getQuestionTypeForStep(step) {
    return stepTypes[step] || 'course-selection';
  }

  // Replace direct URL navigation effect that uses SET_ACTIVE_STEP
  useEffect(() => {
    // Map URL paths to question types
    const pathToTypeMap = {
      '/': 'course-selection',
      '/course-selection': 'course-selection',
      '/state-selection': 'select',
      '/endorsement-selection': 'endorsements',
      '/cdl-type': 'cdl_type',
      '/video-selection': 'video',
      '/course-recommendation': 'recommend',
      '/more-class-selection': 'multi_select_courses',
    };

    // Get current path
    const currentPath = location.pathname;

    // Set question type based on URL if it's a direct navigation
    if (
      pathToTypeMap[currentPath] &&
      formState.question_type !== pathToTypeMap[currentPath]
    ) {
      // console.log(
      //   `Direct navigation to ${currentPath}, setting question_type to ${pathToTypeMap[currentPath]}`
      // );

      // Update form state to match the URL
      setFormState((prev) => ({
        ...prev,
        question_type: pathToTypeMap[currentPath],
        isBackNavigation: false,
      }));

      // Reset to first step if navigating to home
      if (currentPath === '/' || currentPath === '/course-selection') {
        dispatch(resetStep());
      }

      // Set current step in Redux
      dispatch(setCurrentStep(currentPath));
    }
  }, [location.pathname]); // Only re-run when URL changes

  // Initial data fetch only
  useEffect(() => {
    const questionType = getQuestionTypeForStep(0);
    if (!screenData[questionType]) {
      dispatch(fetchDynamicDataRequest({ question_id: formState.question_id }));
    }
  }, []);

  // Effect to handle question_id changes and fetch data
  useEffect(() => {
    if (
      formState.question_id &&
      formState.userAnswer &&
      !formState.isBackNavigation
    ) {
      setFormState((prev) => ({ ...prev, isLoading: true }));
      // For state selection, include the specific payload in the dynamic data request
      if (formState.question_type === 'video') {
        const stateAnswer = formState?.userAnswer;
        dispatch(
          fetchDynamicDataRequest({
            question_id: formState.question_id,
            question_answer_id: stateAnswer?.state_code_question_answer_id,
            state_code: stateAnswer?.state_code,
          })
        );
      } else {
        dispatch(
          fetchDynamicDataRequest({ question_id: formState.question_id })
        );
      }
    }
  }, [formState.question_id, formState.userAnswer]);

  // Update the API response effect to use a single nextStep
  useEffect(() => {
    if (
      dynamicData &&
      formState.userAnswer &&
      formState.isLoading &&
      !formState.isBackNavigation
    ) {
      const nextQuestionType = dynamicData.question_type;
      const currentType = formState.question_type;

      // When moving forward to the next step, dispatch nextStep exactly once
      dispatch(nextStep());

      // Get current path from formState
      const typeToPathMap = {
        'course-selection': '/',
        select: '/state-selection',
        endorsements: '/endorsement-selection',
        cdl_type: '/cdl-type',
        video: '/video-selection',
        recommend: '/course-recommendation',
        multi_select_courses: '/more-class-selection',
      };

      const currentPath = typeToPathMap[currentType] || '/';
      const nextPath = typeToPathMap[nextQuestionType] || '/';

      // Update navigation state
      dispatch({
        type: 'TRACK_NAVIGATION_SEQUENCE',
        payload: {
          currentPath,
          nextPath,
        },
      });

      // Special handling for endorsements - DON'T dispatch another nextStep here
      if (currentType === 'endorsements') {
        if (formState.userAnswer.next_question_id) {
          setFormState((prev) => ({
            ...prev,
            question_type: nextQuestionType,
            isLoading: false,
            isBackNavigation: false,
          }));
          // REMOVE the second dispatch of nextStep
        }
        return;
      }

      // Normal flow for other screens
      if (nextQuestionType && nextQuestionType !== currentType) {
        setFormState((prev) => ({
          ...prev,
          question_type: nextQuestionType,
          isLoading: false,
          isBackNavigation: false,
        }));
      }
    }
  }, [dynamicData]);

  // Listen for navigation changes from the saga
  useEffect(() => {
    // Check if this is manual back navigation
    const isManualBackNavigation =
      window._isManualBackNavigation?.value === true;

    // Only navigate if not handling manual back navigation
    if (
      navigationPath &&
      location.pathname !== navigationPath &&
      !isManualBackNavigation
    ) {
      navigate(navigationPath);
    }
  }, [navigationPath, navigate, location.pathname]);

  // Replace the current step effect that uses SET_ACTIVE_STEP
  useEffect(() => {
    if (currentStep) {
      const stepIndex = stepRoutes.findIndex(
        (route) => route.path === currentStep
      );
      if (stepIndex !== -1 && stepIndex !== activeStep) {
        // Use updateActiveStep instead of direct SET_ACTIVE_STEP dispatch
        updateActiveStep(stepIndex);
      }
    }
  }, [currentStep, dispatch, activeStep]);

  // Add this effect to update navigation state when question_type changes
  useEffect(() => {
    // Skip for initial mount with default state
    if (
      formState.question_type === 'course-selection' &&
      formState.question_id === ''
    ) {
      return;
    }

    // Map the current question type to a path
    const typeToPathMap = {
      'course-selection': '/',
      select: '/state-selection',
      endorsements: '/endorsement-selection',
      cdl_type: '/cdl-type',
      video: '/video-selection',
      recommend: '/course-recommendation',
      multi_select_courses: '/more-class-selection',
    };

    const currentPath = typeToPathMap[formState.question_type] || '/';

    // Only update if it's different from current step
    if (currentPath !== currentStep) {
      // console.log(
      //   `Screen changed to ${formState.question_type}, updating navigation state`
      // );

      // If this is not a back navigation, track it in history
      if (!formState.isBackNavigation) {
        // Find the previous path (current value of currentStep)
        const previousPath = currentStep || '/';

        dispatch({
          type: 'TRACK_NAVIGATION_SEQUENCE',
          payload: {
            currentPath: previousPath,
            nextPath: currentPath,
          },
        });
      }

      // Update navigation state to reflect the current screen
      dispatch(setCurrentStep(currentPath));
    }
  }, [formState.question_type]);

  // Update the browser navigation handler to use single actions
  const handleBrowserNavigation = (event) => {
    const newPath = location.pathname;
    // console.log('Browser navigation detected to:', newPath);

    if (newPath !== currentStep) {
      // console.log('Syncing Redux state with browser navigation');

      // Set back navigation flag
      dispatch({ type: 'SET_IS_BACK_NAVIGATION', payload: true });

      // Determine if this is a step reset
      if (newPath === '/' || newPath === '/course-selection') {
        // Reset to first step
        dispatch(resetStep());
      }
      // We'll handle other navigation changes via the currentStep effect below

      // Find previous step for this path
      const history = Array.isArray(navigationHistory) ? navigationHistory : [];
      const newIndex = history.indexOf(newPath);
      let prevStep = '/';
      if (newIndex > 0) {
        prevStep = history[newIndex - 1];
      }

      // Update navigation state
      dispatch({
        type: 'GO_BACK_IN_NAVIGATION',
        payload: {
          navigationPath: newPath,
          currentStep: newPath,
          previousStep: prevStep,
        },
      });

      // Update form state to match the path
      const pathToTypeMap = {
        '/': 'course-selection',
        '/course-selection': 'course-selection',
        '/state-selection': 'select',
        '/endorsement-selection': 'endorsements',
        '/cdl-type': 'cdl_type',
        '/video-selection': 'video',
        '/course-recommendation': 'recommend',
        '/more-class-selection': 'multi_select_courses',
      };

      setFormState((prev) => ({
        ...prev,
        question_type: pathToTypeMap[newPath] || prev.question_type,
        isBackNavigation: true,
      }));
    }
  };

  // Update the currentStep effect to handle basic next/prev actions
  useEffect(() => {
    if (currentStep && previousStep) {
      // We only need to check if we're moving forward or backward
      const history = Array.isArray(navigationHistory) ? navigationHistory : [];
      const currentIndex = history.indexOf(currentStep);
      const prevIndex = history.indexOf(previousStep);

      // Only dispatch if we haven't already handled this via resetStep
      if (currentStep !== '/' && currentStep !== '/course-selection') {
        if (currentIndex > prevIndex) {
          // Moving forward in history
          // console.log('Moving forward in navigation history');
          // Don't dispatch here - it should be handled by the specific action that triggered this
        } else if (currentIndex < prevIndex) {
          // Moving backward in history
          // console.log('Moving backward in navigation history');
          // Don't dispatch here - handled by handleBack or browser navigation
        }
      }
    }
  }, [currentStep, previousStep]);

  // Update your handleBack function
  const handleBack = () => {
    // console.log('Back button clicked');
    logNavigationState();

    // Only proceed if we have a valid previousStep
    if (previousStep && previousStep !== '/') {
      // console.log('Navigating to previousStep:', previousStep);

      // Simply dispatch PREV_STEP to decrement stepper
      dispatch(prevStep());

      // Set back navigation flag
      dispatch({ type: 'SET_IS_BACK_NAVIGATION', payload: true });

      // Find where the previous step exists in history
      const history = Array.isArray(navigationHistory) ? navigationHistory : [];
      const prevIndex = history.indexOf(previousStep);

      // Determine the "previous previous" step
      let prevPrevStep = '/';
      if (prevIndex > 0) {
        prevPrevStep = history[prevIndex - 1];
      }

      // Update navigation state
      dispatch({
        type: 'GO_BACK_IN_NAVIGATION',
        payload: {
          navigationPath: previousStep,
          currentStep: previousStep,
          previousStep: prevPrevStep,
        },
      });

      // Navigate directly to the previousStep from Redux
      navigate(previousStep);
    } else {
      // console.log('No valid previousStep, navigating to home');

      // Reset stepper to first step
      dispatch({ type: 'RESET_STEP' });

      // Fall back to home page
      dispatch({
        type: 'GO_BACK_IN_NAVIGATION',
        payload: {
          navigationPath: '/',
          currentStep: '/',
          previousStep: '/',
        },
      });

      navigate('/');
    }
  };

  const getComponentByType = (type) => {
    // console.log(type);
    if (formState.isLoading) {
      return (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          minHeight='200px'
        >
          <CircularProgress />
        </Box>
      );
    }

    // Special case for endorsements - don't show loading state
    if (type === 'endorsements' && !formState.userAnswer?.next_question_id) {
      const Component = EndorsementSelection;
      return <Component setFormState={setFormState} handleNext={handleNext} />;
    }

    const componentMap = {
      'course-selection': CourseSelection,
      select: StateSelection,
      endorsements: EndorsementSelection,
      cdl_type: CDLTypeSelection,
      video: VideoSelection,
      recommend: CourseRecommendation,
      multi_select_courses: MoreClassSelection,
    };
    const Component = componentMap[type] || CourseSelection;
    return <Component setFormState={setFormState} handleNext={handleNext} />;
  };

  // Add navigation methods for route-based approach
  const navigateToStep = (path) => {
    navigate(path);
  };

  const handleNext = () => {
    // No action needed here as the state is already set by the child component
    //console.log('next previous Steps:', previousStep);
  };

  const logNavigationState = () => {
    // console.log('=== NAVIGATION STATE ===');
    // console.log('navigationPath:', navigationPath);
    // console.log('currentStep:', currentStep);
    // console.log('previousStep:', previousStep);
    // console.log('currentURL:', location.pathname);
    // console.log('navigationHistory:', navigationHistory);
    // console.log('=======================');
  };

  useEffect(() => {
    // console.log('Stepper state:', {
    //   activeStep,
    //   currentPath: location.pathname,
    //   formQuestionType: formState.question_type,
    //   stepRoutes: stepRoutes.map((r, i) => ({ index: i, path: r.path })),
    // });
  }, [activeStep, location.pathname, formState.question_type]);

  // Add this effect to log activeStep changes
  useEffect(() => {
    // console.log('activeStep changed to:', activeStep);
  }, [activeStep]);

  // Remove the complex updateActiveStep function and replace it with a simpler version
  const updateActiveStep = (newIndex) => {
    // No need for complex logic here - we should just dispatch a single action
    // console.log('Updating active step to:', newIndex);

    // We'll only handle the reset case here
    if (newIndex === 0 && activeStep !== 0) {
      dispatch(resetStep());
    }
    // Don't handle other index changes here - they should be done directly with nextStep/prevStep
  };

  return (
    <Box className='steps-bar-and-content-box-wrapper'>
      <Banner />
      {/* Progress Stepper */}
      <Box
        className='steps-progress-bar'
        sx={{ width: '100%', margin: '0 auto' }}
      >
        <Stepper
          className='steps-progress-bar'
          activeStep={activeStep}
          alternativeLabel
          connector={<CustomConnector />}
        >
          {steps?.map((step, index) => (
            <Step key={index}>
              <StepLabel StepIconComponent={CustomStepIcon}></StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Content Area - Use component selection instead of Outlet */}
      <Box className='cdl-step-box-wrap'>
        {getComponentByType(formState.question_type)}
        <Box mt={2} className='step-button-wrap'>
          <Button
            className={
              formState.question_type === 'endorsements' ||
              formState.question_type === 'multi_select_courses'
                ? 'back-btn'
                : ''
            }
            disabled={
              !currentStep || currentStep === '/' || formState.isLoading
            }
            onClick={handleBack}
            sx={{
              mr: 1,
              color: '#01418C',
              border: '2px solid #01418C',
              borderRadius: '8px',
              padding: '8px 24px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#01418C',
                color: '#fff',
                border: '2px solid #01418C',
              },
              '&:active': {
                backgroundColor: '#013169',
                borderColor: '#013169',
                color: '#fff',
              },
              '&.Mui-disabled': {
                backgroundColor: '#F5F5F5',
                color: '#BDBDBD',
                border: '2px solid #BDBDBD',
              },
            }}
          >
            <ChevronLeftOutlinedIcon />
            Back
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default WizardLaunchStepper;
